import { isDatalessWidgetType, isReadmodelWidgetType, WidgetDefinition, WidgetType } from '../types';
import { ValidationErrors } from '../validateWidget';
import { hasUnjustifiedKpiMeasures, validateReadmodelBinding } from '../WebWidget/WebWidgetValidators';
import { TFunction } from 'i18next';

// Common
const validateCommon = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {};
  if ( !widgetConfig.name?.trim()) {
    errors.basic = { ...( errors.basic ?? {}), name: t( 'widget_builder_validation_name_required', 'Name is required' ) };
  }
  // TV1-1930 — the Mobile twin of the web guard. `text` is web-only, so this
  // branch is not reachable through the mobile builder today; it exists so the two
  // validators cannot DISAGREE about what a dataless widget requires if `text` (or
  // a future dataless type) ever becomes mobile-capable.
  //
  // TV1-2291 (t25) — same story for the four readmodel types, and the same reason to
  // mirror the web guard rather than leave the twins asymmetric: `count` / `alert` /
  // `list` / `progress` extend `UiWidgetReadmodelBaseSchema`, so they carry no `cube`
  // and no `measures` FIELDS to satisfy.
  if ( !isDatalessWidgetType( widgetConfig.type ) && !isReadmodelWidgetType( widgetConfig.type )) {
    if ( !widgetConfig.cube?.trim()) {
      errors.basic = { ...( errors.basic ?? {}), cube: t( 'widget_builder_validation_datasource_required', 'Data source is required' ) };
    }
    if ( !widgetConfig.measures?.length ) {
      errors.basic = { ...( errors.basic ?? {}), measures: t( 'widget_builder_validation_measures_required', 'At least one measure is required' ) };
    }
  }
  if ( !widgetConfig.type ) {
    errors.basic = { ...( errors.basic ?? {}), type: t( 'widget_builder_validation_type_required', 'Widget type is required' ) };
  }
  return Object.keys( errors ).length ? errors : null;
};

// Cartesian Validation
const validateCartesianWidgetProps = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {};
  const props = widgetConfig.widgetComponentProps || {};
  if ( !props.xAxis?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), xAxis: t( 'widget_builder_validation_xaxis_required', 'X-Axis is required' ) };
  }
  if ( !props.yAxis?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), yAxis: t( 'widget_builder_validation_yaxis_required', 'Y-Axis is required' ) };
  }
  return Object.keys( errors ).length ? errors : null;
};

// Pie/Donut Validation
const validatePieDonut = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors = validateCommon( widgetConfig, t ) || {};
  const props = widgetConfig.widgetComponentProps || {};
  if ( !props.groupBy?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), groupBy: t( 'widget_builder_validation_groupby_required', 'Group by is required' ) };
  }
  if ( !props.valueMetric?.trim()) {
    errors.widgetProps = { ...( errors.widgetProps ?? {}), valueMetric: t( 'widget_builder_validation_valuemetric_required', 'Value metric is required' ) };
  }
  return Object.keys( errors ).length ? errors : null;
};

// Mobile Validators
const validateAreaChart = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );
const validateBarChart = ( config: WidgetDefinition, t: TFunction ) => {
  const errors = validateCommon( config, t ) || {};
  Object.assign( errors, validateCartesianWidgetProps( config, t ));
  return Object.keys( errors ).length ? errors : null;
};
const validateLineChart = validateBarChart;
const validatePieChart = validatePieDonut;
const validateDonutChart = validatePieDonut;
const validateScatterChart = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );
// TV1-1933 (S18) — criticalStock is web-only (absent from mobileWidgetTypes), but
// the map is a total Record<WidgetType>, so it needs an entry; common-only.
const validateCriticalStock = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );
// TV1-1929 (S14) — same story for `table`: web-only, but `widgetValidatorsMobile`
// is a TOTAL Record<WidgetType>, so omitting it is a COMPILE error. Common-only.
const validateTable = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );
// TV1-1930 (S15) — `kpi`, unlike the two above, IS mobile-capable, so this entry
// is genuinely reachable. It carries the SAME measure tightening as the web
// validator: a KPI is a single number on any device, and a second DISPLAYED
// measure would be silently dropped.
//
// TV1-2291 (t29) — that rule is now "exactly one DISPLAYED measure": a declared
// `widgetComponentProps.activityMeasure` witness legitimises `measures[1]`. The
// predicate is IMPORTED rather than re-stated so the two devices cannot disagree
// about what a KPI may query — see `hasUnjustifiedKpiMeasures` for the rule and
// why the witness is pinned to that position.
const validateKpi = ( config: WidgetDefinition, t: TFunction ) => {
  const errors = validateCommon( config, t ) || {};
  if ( hasUnjustifiedKpiMeasures( config )) {
    errors.basic = {
      ...( errors.basic ?? {}),
      measures: t( 'widget_builder_validation_kpi_single_measure', 'A KPI shows exactly one measure' )
    };
  }
  return Object.keys( errors ).length ? errors : null;
};

// TV1-1930 (S15) — `text` is WEB-ONLY, but `widgetValidatorsMobile` is a TOTAL
// Record<WidgetType>, so an entry is REQUIRED even so: omitting it is a compile
// error, not a silent gap. Common-only (which, for a dataless type, is just "it
// needs a name").
const validateText = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );

// TV1-2291 (t21) — `heading` and `spacer` are WEB-ONLY too (absent from
// `mobileWidgetTypes`: a phone report is one column, so there is nothing for a
// section rule or a deliberate gap to divide). Same story as `text`/`table`/
// `criticalStock` above — this map is a TOTAL Record<WidgetType>, so omitting them
// is a COMPILE error rather than a silent gap. Common-only, which for a dataless
// type is just "it needs a name".
const validateHeading = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );
const validateSpacer = ( config: WidgetDefinition, t: TFunction ) => validateCommon( config, t );

// TV1-2291 (t25) — the four READMODEL widgets. WEB-ONLY, like `text` / `table` /
// `criticalStock` / `heading` / `spacer`: they are absent from `mobileWidgetTypes`,
// so these entries are not reachable through the mobile builder. They exist because
// this map is a TOTAL `Record<WidgetType>` and omitting them is a COMPILE error
// rather than a silent gap.
//
// ⚠️ The binding rule is IMPORTED, not restated. A hand-copied "a readmodel card
// needs a collection" would be the second definition, and the two would drift the
// first time a readmodel widget schema gains a required field — exactly the reason
// `hasUnjustifiedKpiMeasures` is imported above rather than re-written here.
//
// `validateCommon` (the mobile twin) already skips the cube/measures preconditions
// for a readmodel type, via the same `isReadmodelWidgetType` guard the web one uses.
const validateReadmodelWidgetType = ( config: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const errors: ValidationErrors = {
    ...( validateCommon( config, t ) ?? {}),
    ...( validateReadmodelBinding( config, t ) ?? {})
  };
  return Object.keys( errors ).length ? errors : null;
};

export const widgetValidatorsMobile: Record<WidgetType, ( config: WidgetDefinition, t: TFunction ) => ValidationErrors | null> = {
  areaChart: validateAreaChart,
  barChart:  validateBarChart,
  lineChart: validateLineChart,
  pieChart:  validatePieChart,
  donutChart:validateDonutChart,
  scatterChart: validateScatterChart,
  criticalStock: validateCriticalStock,
  table: validateTable,
  kpi: validateKpi,
  text: validateText,
  heading: validateHeading,
  spacer: validateSpacer,
  count: validateReadmodelWidgetType,
  alert: validateReadmodelWidgetType,
  list: validateReadmodelWidgetType,
  progress: validateReadmodelWidgetType
};

export const validateMobileWidget = ( widgetConfig: WidgetDefinition, t: TFunction ): ValidationErrors | null => {
  const validator = widgetValidatorsMobile[widgetConfig.type as WidgetType];
  if ( !validator ) {
    return { basic: { type: t( 'widget_builder_validation_invalid_type', 'Invalid widget type: {{type}}', { type: widgetConfig.type }) }};
  }
  return validator( widgetConfig, t );
};
